/**
 * NiV Lead Page — /dashboard/niv/lead/:phoneKey  (phoneKey = E164 without "+")
 *
 * Replaces the old prospect drawer. One lead, everything on one screen:
 *  header (name · phone · stage · delete) → status + details + linked clinic
 *  (+ payment panel once linked) | free notes + AI chat summary + history.
 *
 * Reads the lead from the shared getProspectsBoard cache (same query the leads
 * list uses), so mutations reflect immediately and nothing is fetched twice.
 */

import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ArrowRight,
  Phone,
  WhatsappLogo,
  Trash,
  NotePencil,
  Check,
  X,
  ShieldCheck,
  ShieldSlash,
  Buildings,
  LinkSimple,
  LinkBreak,
  CaretDown,
  CaretUp,
  ClockCounterClockwise,
  ChatText,
} from "@phosphor-icons/react";
import ContentLoader from "../../../../components/shared/ContentLoader";
import { selectIsNivAdmin, selectTherapistName } from "../../../../store/slices/authSlice";
import { useToast } from "../../../../contexts/ToastContext";
import ConfirmDialog from "../../../../components/ui/ConfirmDialog";
import {
  useGetProspectsBoardQuery,
  useGetNivClinicsOverviewQuery,
  useUpdateProspectDetailsMutation,
  useDeleteProspectMutation,
  useApproveProspectRegistrationMutation,
  useRevokeProspectRegistrationMutation,
  useUnlinkProspectFromClinicMutation,
  useAddProspectNoteMutation,
  useUpdateProspectFreeNotesMutation,
  useUpdateProspectNextActionMutation,
} from "../../../../store/services/nivApi";
import {
  PROSPECT_STAGES,
  PROSPECT_STAGE_LABELS,
  PROSPECT_STAGE_COLORS,
  PROSPECT_SOURCE_LABELS,
} from "../../../../services/nivFirebase";
import { formatPhoneIL } from "../../../../utils/phoneUtils";
import { formatDate, formatUtmSummary } from "../shared/nivDerived";
import { getProfession } from "../../../../data/startPageContent";
import NivNotesThread from "../shared/NivNotesThread";
import ClinicPaymentPanel from "../shared/ClinicPaymentPanel";
import FreeNotesCard from "../shared/FreeNotesCard";
import NextActionCard from "../shared/NextActionCard";
import AiChatSummaryCard from "../shared/AiChatSummaryCard";
import ProspectStageControl from "../clients/ProspectStageControl";
import LinkClinicPicker from "./LinkClinicPicker";
import "../clients/NivClientsPage.css";
import "./NivLeadPage.css";

const LEADS_PATH = "/dashboard/niv/clients";

const formatDateTime = (dateString) => {
  if (!dateString) return null;
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const rtkErrorMessage = (error) =>
  error?.message || error?.data?.message || "אירעה שגיאה, נסה שוב";

function NivLeadPage() {
  const { phoneKey } = useParams();
  const navigate = useNavigate();
  const isAuthorized = useSelector(selectIsNivAdmin);
  const therapistName = useSelector(selectTherapistName);
  const author = therapistName || "NiV";
  const { showSuccess, showError } = useToast();

  const phone = phoneKey ? `+${String(phoneKey).replace(/^\+/, "")}` : null;

  const { data: board, isLoading } = useGetProspectsBoardQuery(undefined, { skip: !isAuthorized });
  const { data: clinicRows = [] } = useGetNivClinicsOverviewQuery(undefined, { skip: !isAuthorized });

  const prospect = useMemo(
    () => (board?.prospects || []).find((p) => p.id === phone) || null,
    [board, phone]
  );
  const linkedClinic = useMemo(
    () => (prospect?.clinicId ? clinicRows.find((r) => r.clinicId === prospect.clinicId) || null : null),
    [clinicRows, prospect?.clinicId]
  );

  const [updateDetails, { isLoading: isSavingDetails }] = useUpdateProspectDetailsMutation();
  const [deleteProspect, { isLoading: isDeleting }] = useDeleteProspectMutation();
  const [approveRegistration, { isLoading: isApproving }] = useApproveProspectRegistrationMutation();
  const [revokeRegistration, { isLoading: isRevoking }] = useRevokeProspectRegistrationMutation();
  const [unlinkProspect, { isLoading: isUnlinking }] = useUnlinkProspectFromClinicMutation();
  const [addNote, { isLoading: isAddingNote }] = useAddProspectNoteMutation();
  const [saveFreeNotes] = useUpdateProspectFreeNotesMutation();
  const [saveNextAction] = useUpdateProspectNextActionMutation();

  const [isEditing, setIsEditing] = useState(false);
  const [editFields, setEditFields] = useState({ fullName: "", clinicName: "", email: "" });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showRequests, setShowRequests] = useState(false);

  const goBack = () => navigate(LEADS_PATH);

  // ---------- handlers ----------

  const startEditing = () => {
    setEditFields({
      fullName: prospect.fullName || "",
      clinicName: prospect.clinicName || "",
      email: prospect.email || "",
    });
    setIsEditing(true);
  };

  const handleSaveDetails = async () => {
    try {
      await updateDetails({
        phone: prospect.id,
        updates: {
          fullName: editFields.fullName.trim() || null,
          clinicName: editFields.clinicName.trim() || null,
          email: editFields.email.trim().toLowerCase() || null,
        },
      }).unwrap();
      setIsEditing(false);
      showSuccess("הפרטים נשמרו");
    } catch (error) {
      console.error("[CK-9996] Error saving prospect details:", {
        errorCode: "CK-9996",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError(rtkErrorMessage(error));
    }
  };

  const handleDelete = async () => {
    try {
      await deleteProspect({ phone: prospect.id, callRequestIds: prospect.callRequestIds || [] }).unwrap();
      showSuccess("הליד נמחק");
      goBack();
    } catch (error) {
      console.error("[CK-9988] Error deleting prospect:", {
        errorCode: "CK-9988",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError(rtkErrorMessage(error));
    }
  };

  const handleToggleRegistration = async () => {
    try {
      if (prospect.isWhitelisted) {
        await revokeRegistration({ phone: prospect.id, author }).unwrap();
        showSuccess("ההרשאה להרשמה הוסרה");
      } else {
        await approveRegistration({ phone: prospect.id, addedByName: author }).unwrap();
        showSuccess("הליד אושר להרשמה");
      }
    } catch (error) {
      console.error("[CK-9818] Error toggling prospect registration access:", {
        errorCode: "CK-9818",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError(rtkErrorMessage(error));
    }
  };

  const handleUnlink = async () => {
    try {
      await unlinkProspect({ phone: prospect.id, author }).unwrap();
      setShowUnlinkConfirm(false);
      showSuccess("הקישור לקליניקה בוטל");
    } catch (error) {
      console.error("[CK-9807] Error unlinking prospect:", {
        errorCode: "CK-9807",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError(rtkErrorMessage(error));
    }
  };

  const handleAddNote = async (text) => {
    try {
      await addNote({ phone: prospect.id, text, author }).unwrap();
      return true;
    } catch (error) {
      console.error("[CK-9979] Error adding prospect note:", {
        errorCode: "CK-9979",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError(rtkErrorMessage(error));
      return false;
    }
  };

  const handleSaveNextAction = async ({ text, at }) => {
    try {
      await saveNextAction({ phone: prospect.id, text, at }).unwrap();
    } catch (error) {
      console.error("[CK-9822] Error saving lead next action:", {
        errorCode: "CK-9822",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  };

  const handleSaveFreeNotes = async (text) => {
    try {
      await saveFreeNotes({ phone: prospect.id, text }).unwrap();
    } catch (error) {
      console.error("[CK-9808] Error saving lead free notes:", {
        errorCode: "CK-9808",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  };

  // ---------- render ----------

  if (!isAuthorized) return null;

  if (isLoading && !board) {
    return (
      <div className="nlp-page">
        <ContentLoader fill message="טוען ליד…" />
      </div>
    );
  }

  if (!prospect) {
    return (
      <div className="nlp-page">
        <div className="nlp-topbar">
          <button type="button" className="nlp-back" onClick={goBack} aria-label="חזרה ללידים">
            <ArrowRight size={18} weight="bold" />
          </button>
          <h2 className="nlp-title">ליד לא נמצא</h2>
        </div>
        <div className="nlp-empty">הליד {phone ? formatPhoneIL(phone) : ""} לא קיים או נמחק.</div>
      </div>
    );
  }

  const stage = prospect.stage || PROSPECT_STAGES.LEAD;
  const stageColor = PROSPECT_STAGE_COLORS[stage] || "#9ca3af";
  const isLost = stage === PROSPECT_STAGES.LOST;
  const stageHistory = [...(prospect.stageHistory || [])].reverse();
  const callRequests = prospect.callRequests || [];
  const utmSummary = formatUtmSummary(prospect.utm);
  const notes = prospect.notes || [];
  const waDigits = prospect.id.replace(/\D/g, "");
  const isWhitelisted = prospect.isWhitelisted === true;
  const isTogglingRegistration = isApproving || isRevoking;

  return (
    <div className="nlp-page">
      {/* Top bar */}
      <div className="nlp-topbar">
        <button type="button" className="nlp-back" onClick={goBack} aria-label="חזרה ללידים">
          <ArrowRight size={18} weight="bold" />
        </button>
        <div className="nlp-topbar-main">
          <h2 className="nlp-title">{prospect.fullName || formatPhoneIL(prospect.id)}</h2>
          <div className="nlp-topbar-meta">
            <a className="nlp-link" href={`tel:${prospect.id}`} dir="ltr">
              <Phone size={13} weight="fill" /> {formatPhoneIL(prospect.id)}
            </a>
            <a
              className="nlp-link nlp-link-wa"
              href={`https://wa.me/${waDigits}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <WhatsappLogo size={13} weight="fill" /> וואטסאפ
            </a>
            <span className="nlp-badge" style={{ backgroundColor: `${stageColor}1f`, color: stageColor, borderColor: `${stageColor}55` }}>
              {PROSPECT_STAGE_LABELS[stage] || stage}
            </span>
            {prospect.source && (
              <span className="nlp-badge nlp-badge-muted">
                {PROSPECT_SOURCE_LABELS[prospect.source] || prospect.source}
              </span>
            )}
            {prospect.profession && (
              <span className="nlp-badge nlp-badge-muted" title="תחום הטיפול שנבחר בדף /start">
                {getProfession(prospect.profession)?.label || prospect.profession}
              </span>
            )}
            {utmSummary && (
              <span className="nlp-badge nlp-badge-muted" title="מקור קמפיין (UTM)">
                {utmSummary}
              </span>
            )}
            {prospect.existsInSystem && !prospect.clinicId && (
              <span className="nlp-badge nlp-badge-success" title="מספר זה רשום במערכת">
                <ShieldCheck size={11} weight="fill" /> קיים במערכת
                {prospect.matchedTherapistName ? ` · ${prospect.matchedTherapistName}` : ""}
              </span>
            )}
            {!prospect.existsInSystem && (
              isWhitelisted ? (
                <span className="nlp-badge nlp-badge-success" title="המספר נמצא ברשימת המורשים להרשמה">
                  <ShieldCheck size={11} weight="fill" /> מורשה להרשמה
                </span>
              ) : (
                <span className="nlp-badge nlp-badge-warning" title="המספר אינו ברשימת המורשים — ההרשמה תיחסם">
                  <ShieldSlash size={11} weight="fill" /> לא מורשה להרשמה
                </span>
              )
            )}
          </div>
        </div>
        <button
          type="button"
          className="nlp-danger-btn"
          onClick={() => setShowDeleteConfirm(true)}
          disabled={isDeleting}
        >
          <Trash size={14} /> מחק ליד
        </button>
      </div>

      <div className="nlp-columns">
        {/* ===== Column A ===== */}
        <div className="nlp-col">
          {/* Status */}
          <section className="nlp-card">
            <ProspectStageControl prospect={prospect} author={author} />
            {isLost && prospect.lostReason && (
              <div className="nlp-lost-reason">סיבה: {prospect.lostReason}</div>
            )}

            {/* Registration access (whitelist) — live truth, togglable */}
            {!prospect.existsInSystem && (
              <div className={`nlp-access${isWhitelisted ? " is-allowed" : ""}`}>
                <span className="nlp-access-icon">
                  {isWhitelisted ? <ShieldCheck size={16} weight="fill" /> : <ShieldSlash size={16} weight="fill" />}
                </span>
                <div className="nlp-access-text">
                  <strong>{isWhitelisted ? "מורשה להרשמה" : "לא מורשה להרשמה"}</strong>
                  <span>
                    {isWhitelisted
                      ? `המספר ברשימת המורשים${prospect.whitelistedAt ? ` מ-${formatDate(prospect.whitelistedAt)}` : ""}. ניתן להירשם עם ${formatPhoneIL(prospect.id)}.`
                      : "ההרשמה היא בהזמנה בלבד — עד שתאשר, הליד ייחסם במסך ההרשמה."}
                  </span>
                </div>
                <button
                  type="button"
                  className={isWhitelisted ? "nivcl-ghost-btn" : "nivcl-primary-btn"}
                  onClick={handleToggleRegistration}
                  disabled={isTogglingRegistration}
                >
                  {isTogglingRegistration ? "שומר…" : isWhitelisted ? "הסר הרשאה" : "אשר להרשמה"}
                </button>
              </div>
            )}
            {stageHistory.length > 0 && (
              <div className="nlp-history">
                {stageHistory.slice(0, 5).map((h, i) => (
                  <span key={`${h.stage}-${h.at}-${i}`} className="nlp-history-item">
                    <span className="nivcl-stage-dot" style={{ backgroundColor: PROSPECT_STAGE_COLORS[h.stage] || "#9ca3af" }} />
                    {PROSPECT_STAGE_LABELS[h.stage] || h.stage}
                    <span className="nlp-history-date" dir="ltr">{formatDateTime(h.at)}</span>
                    {h.by && h.by !== "sync" && h.by !== "system" && <span className="nlp-history-by">· {h.by}</span>}
                  </span>
                ))}
              </div>
            )}
          </section>

          {/* Details */}
          <section className="nlp-card">
            <div className="nlp-card-head">
              <h4>פרטים</h4>
              {!isEditing ? (
                <button type="button" className="nivcl-icon-btn" onClick={startEditing} aria-label="עריכת פרטים">
                  <NotePencil size={15} />
                </button>
              ) : (
                <div className="nivcl-edit-actions">
                  <button type="button" className="nivcl-icon-btn nivcl-icon-btn-confirm" onClick={handleSaveDetails} disabled={isSavingDetails} aria-label="שמירה">
                    <Check size={15} weight="bold" />
                  </button>
                  <button type="button" className="nivcl-icon-btn" onClick={() => setIsEditing(false)} aria-label="ביטול עריכה">
                    <X size={15} />
                  </button>
                </div>
              )}
            </div>
            {isEditing ? (
              <div className="nivcl-fields">
                <label className="nivcl-field">
                  <span>שם מלא</span>
                  <input type="text" value={editFields.fullName} onChange={(e) => setEditFields((f) => ({ ...f, fullName: e.target.value }))} />
                </label>
                <label className="nivcl-field">
                  <span>שם הקליניקה</span>
                  <input type="text" value={editFields.clinicName} onChange={(e) => setEditFields((f) => ({ ...f, clinicName: e.target.value }))} />
                </label>
                <label className="nivcl-field">
                  <span>אימייל</span>
                  <input type="email" dir="ltr" value={editFields.email} onChange={(e) => setEditFields((f) => ({ ...f, email: e.target.value }))} />
                </label>
              </div>
            ) : (
              <div className="nivcl-details-grid">
                <div className="nivcl-detail-item">
                  <span className="nivcl-detail-label">שם הקליניקה</span>
                  <span className="nivcl-detail-value">{prospect.clinicName || "—"}</span>
                </div>
                <div className="nivcl-detail-item">
                  <span className="nivcl-detail-label">אימייל</span>
                  <span className="nivcl-detail-value" dir="ltr">{prospect.email || "—"}</span>
                </div>
                <div className="nivcl-detail-item">
                  <span className="nivcl-detail-label">נוצר</span>
                  <span className="nivcl-detail-value">{formatDate(prospect.createdAt)}</span>
                </div>
                <div className="nivcl-detail-item">
                  <span className="nivcl-detail-label">פעילות אחרונה</span>
                  <span className="nivcl-detail-value">{formatDate(prospect.updatedAt)}</span>
                </div>
                {prospect.profession && (
                  <div className="nivcl-detail-item">
                    <span className="nivcl-detail-label">תחום</span>
                    <span className="nivcl-detail-value">{getProfession(prospect.profession)?.label || prospect.profession}</span>
                  </div>
                )}
                {prospect.utm && (
                  <div className="nivcl-detail-item">
                    <span className="nivcl-detail-label">קמפיין</span>
                    <span className="nivcl-detail-value" dir="ltr">
                      {["source", "medium", "campaign", "term", "content"]
                        .filter((k) => prospect.utm[k])
                        .map((k) => `${k}=${prospect.utm[k]}`)
                        .join(" · ") || utmSummary}
                    </span>
                  </div>
                )}
                {prospect.lastMessage && (
                  <div className="nivcl-detail-item nivcl-detail-item--wide">
                    <span className="nivcl-detail-label">הודעה מהליד</span>
                    <span className="nivcl-detail-value">{prospect.lastMessage}</span>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Linked clinic */}
          <section className="nlp-card">
            <div className="nlp-card-head">
              <h4>קליניקה</h4>
            </div>
            {prospect.clinicId ? (
              <div className="nlp-linked">
                <div className="nlp-linked-main">
                  <span className="nlp-linked-icon">
                    <Buildings size={16} weight="fill" />
                  </span>
                  <div className="nlp-linked-titles">
                    <strong>{linkedClinic?.name || prospect.clinicName || "קליניקה"}</strong>
                    <span className="nlp-linked-meta">
                      {linkedClinic?.planName || ""}
                      {linkedClinic?.planEndDate ? ` · פעיל עד ${formatDate(linkedClinic.planEndDate)}` : ""}
                      {prospect.linkedAt ? ` · קושר ${formatDate(prospect.linkedAt)}` : prospect.registeredAt ? ` · נרשם ${formatDate(prospect.registeredAt)}` : ""}
                    </span>
                  </div>
                </div>
                <div className="nlp-linked-actions">
                  <button type="button" className="nivcl-primary-btn" onClick={() => navigate(`/dashboard/niv/clinic/${prospect.clinicId}`)}>
                    פתח כרטיס קליניקה
                  </button>
                  <button type="button" className="nivcl-ghost-btn" onClick={() => setShowUnlinkConfirm(true)} disabled={isUnlinking}>
                    <LinkBreak size={14} /> נתק קישור
                  </button>
                </div>
              </div>
            ) : (
              <div className="nlp-unlinked">
                <span className="nlp-muted">הליד עדיין לא מקושר לקליניקה.</span>
                <button type="button" className="nivcl-secondary-btn" onClick={() => setShowPicker(true)}>
                  <LinkSimple size={14} /> קשר לקליניקה קיימת
                </button>
              </div>
            )}
          </section>

          {/* Payment (only when linked) */}
          {prospect.clinicId && linkedClinic && (
            <ClinicPaymentPanel clinic={linkedClinic} crm={linkedClinic.crm} />
          )}
        </div>

        {/* ===== Column B ===== */}
        <div className="nlp-col">
          <FreeNotesCard
            value={prospect.freeNotes}
            updatedAt={prospect.freeNotesUpdatedAt}
            onSave={handleSaveFreeNotes}
          />

          <NextActionCard value={prospect.nextAction} onSave={handleSaveNextAction} />

          <AiChatSummaryCard target={{ kind: "prospect", id: prospect.id }} aiSummary={prospect.aiSummary} />

          {/* Updates thread (collapsed) */}
          <section className="nlp-card">
            <button type="button" className="nlp-collapse-head" onClick={() => setShowHistory((v) => !v)} aria-expanded={showHistory}>
              <span className="nlp-inline">
                <ClockCounterClockwise size={14} /> היסטוריית עדכונים ({notes.length})
              </span>
              {showHistory ? <CaretUp size={14} /> : <CaretDown size={14} />}
            </button>
            {showHistory && (
              <NivNotesThread notes={notes} onAdd={handleAddNote} isAdding={isAddingNote} emptyLabel="אין עדכונים עדיין" />
            )}
          </section>

          {/* Linked call requests (collapsed) */}
          {callRequests.length > 0 && (
            <section className="nlp-card">
              <button type="button" className="nlp-collapse-head" onClick={() => setShowRequests((v) => !v)} aria-expanded={showRequests}>
                <span className="nlp-inline">
                  <ChatText size={14} /> פניות מקושרות ({callRequests.length})
                </span>
                {showRequests ? <CaretUp size={14} /> : <CaretDown size={14} />}
              </button>
              {showRequests && (
                <div className="nivcl-requests-list">
                  {callRequests.map((req) => (
                    <div key={req.id} className="nivcl-request-item">
                      <div className="nivcl-request-head">
                        <span className="nivcl-card-source">
                          {PROSPECT_SOURCE_LABELS[req.source] || req.source || "פנייה"}
                        </span>
                        <span className="nivcl-request-date">{formatDateTime(req.createdAt) || "—"}</span>
                      </div>
                      {req.message && <p className="nivcl-request-msg">{req.message}</p>}
                      {(req.conversationLogs || []).map((log) => (
                        <div key={log.id} className="nivcl-request-log">
                          <span>{log.text}</span>
                          <span className="nivcl-request-date">{formatDateTime(log.createdAt) || ""}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      </div>

      {showPicker && (
        <LinkClinicPicker prospect={prospect} author={author} onClose={() => setShowPicker(false)} />
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="מחיקת ליד"
        message={
          prospect.clinicId
            ? "הליד מקושר לקליניקה. המחיקה מסירה את רשומת הליד, הפניות המקושרות וההרשאה להרשמה — הקליניקה והתשלומים שלה נשארים. להמשיך?"
            : "האם למחוק את הליד לצמיתות? יימחקו גם הפניות המקושרות וההרשאה להרשמה. פעולה זו אינה הפיכה."
        }
        confirmText="מחק"
        type="danger"
        icon="delete"
      />

      <ConfirmDialog
        isOpen={showUnlinkConfirm}
        onClose={() => setShowUnlinkConfirm(false)}
        onConfirm={handleUnlink}
        title="ניתוק מהקליניקה"
        message="הליד יחזור לשלב הקודם בתהליך. פרטי הקליניקה והתשלומים לא נמחקים."
        confirmText="נתק"
        type="warning"
        icon="warning"
      />
    </div>
  );
}

export default NivLeadPage;
